import type { FormConfiguration } from '@store/models/booking';
// Raw SVG markup (?raw) so FormStep can inline the glyphs and recolor them via
// `currentColor`/theme tokens. The source SVGs ship with a baked-in navy fill
// that is invisible in dark mode; inlining lets the stepper-matching blue apply.
import locationIcon from '@/assets/icons/figma/step-1/location.svg?raw';
import calendarIcon from '@/assets/icons/figma/step-1/calendar.svg?raw';
import peopleIcon from '@/assets/icons/figma/step-1/people.svg?raw';
import eventInfoIcon from '@/assets/icons/figma/step-3/event-info.svg?raw';
import sponsorshipIcon from '@/assets/icons/figma/step-3/sponsorship.svg?raw';
import onsiteContactIcon from '@/assets/icons/figma/step-3/onsite-contact.svg?raw';
import complianceIcon from '@/assets/icons/figma/step-3/compliance.svg?raw';
import cateringIcon from '@/assets/icons/figma/step-4/catering.svg?raw';
import filmingIcon from '@/assets/icons/figma/step-4/filming.svg?raw';
import overnightStorageIcon from '@/assets/icons/figma/step-4/overnight-storage.svg?raw';
import shipmentsIcon from '@/assets/icons/figma/step-4/shipments.svg?raw';
import additionalInfoIcon from '@/assets/icons/figma/step-4/additional-info.svg?raw';

export const DEFAULT_POLICIES_URL = '/policies/conference-room';

export const ATTENDEE_TYPE_OPTIONS = [
  { value: 'INTERNAL', labelKey: 'Microsoft FTEs only' },
  { value: 'MIXED', labelKey: 'Microsoft FTEs + External guests' },
  { value: 'EXTERNAL', labelKey: 'External guests only' },
  { value: 'VIP', labelKey: 'VIP / Executive visitors' },
];

export const VISIT_PURPOSE_OPTIONS = [
  { value: 'A', labelKey: 'Academic' },
  { value: 'C', labelKey: 'Community Affairs' },
  { value: 'E', labelKey: 'Executive' },
  { value: 'G', labelKey: 'Government' },
  { value: 'H', labelKey: 'Human Resources' },
  { value: 'MC', labelKey: 'MS Customer' },
  { value: 'MI', labelKey: 'MS Internal' },
  { value: 'MP', labelKey: 'MS Partner' },
  { value: 'NB', labelKey: 'Non-MS Business' },
  { value: 'NP', labelKey: 'Non-Profit' },
  { value: 'T', labelKey: 'Training' },
  { value: 'UG', labelKey: 'User Group' },
  { value: 'W', labelKey: 'Walk Ins' },
];

export const bookingFormConfig: FormConfiguration = {
  id: 'primary-booking-form',

  quickLinks: [
    {
      labelKey: 'bookingForm.quickLinks.wifiGuests',
      url: '/policies/wifi-guests',
      icon: 'Wifi1Regular',
    },
    { labelKey: 'bookingForm.quickLinks.refWeb', url: '/ref-web', icon: 'GlobeRegular' },
    {
      labelKey: 'bookingForm.quickLinks.facilitiesPolicy',
      url: '/policies/gws-facilities',
      icon: 'ShieldRegular',
    },
    {
      labelKey: 'bookingForm.quickLinks.gmsTemplate',
      url: '/templates/gms',
      icon: 'CalendarLtrRegular',
    },
    {
      labelKey: 'bookingForm.quickLinks.visitorPolicy',
      url: 'https://microsoft.sharepoint.com/sites/globalsecurity/SitePages/Awareness-AccessControl-Visitors.aspx',
      icon: 'PersonRegular',
    },
    {
      labelKey: 'bookingForm.quickLinks.conferenceRoomPolicy',
      url: '/policies/conference-room',
      icon: 'DocumentRegular',
    },
    {
      labelKey: 'bookingForm.quickLinks.buildingAccess',
      url: 'https://microsoft.sharepoint.com/sites/globalsecurity/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2Fglobalsecurity%2FShared%20Documents%2FGSAM%2FMicrosoft%20Building%20Access%20Requirements%2Epdf&parent=%2Fsites%2Fglobalsecurity%2FShared%20Documents%2FGSAM&share=cQqMU%2Ba4A2RHQZBNl2LD7%5FkNEgUCk6zMzEA7mV%2DFFdH%2DQ41l0A',
      icon: 'BuildingRegular',
    },
  ],

  steps: [
    // Step 1: Book a Room - sections per Figma node 1-1038
    {
      id: 'locationDateTime',
      titleKey: 'bookingForm.steps.locationDateTime.title',
      descriptionKey: 'bookingForm.steps.locationDateTime.description',
      bannerTitleKey: 'bookingForm.steps.locationDateTime.bannerTitle',
      bannerDescriptionKey: 'bookingForm.steps.locationDateTime.bannerDescription',
      type: 'form',
      section: 'locationDateTime',
      sections: [
        {
          headerKey: 'bookingForm.sections.where',
          descriptionKey: 'bookingForm.sections.whereDescription',
          iconSrc: locationIcon,
          iconTone: 'primary',
          fields: [
            {
              key: 'stateProvince',
              type: 'dropdown',
              labelKey: 'bookingForm.fields.stateProvince.label',
              placeholderKey: 'bookingForm.fields.stateProvince.placeholder',
              colspan: 1,
              validation: [{ type: 'required', messageKey: 'bookingForm.validation.required' }],
              options: [],
            },
            {
              key: 'buildingId',
              type: 'dropdown',
              labelKey: 'bookingForm.fields.buildingId.label',
              placeholderKey: 'bookingForm.fields.buildingId.placeholder',
              colspan: 1,
              validation: [{ type: 'required', messageKey: 'bookingForm.validation.required' }],
              visibleWhen: { fieldKey: 'stateProvince', operator: 'notEmpty' },
              options: [],
            },
          ],
        },
        {
          headerKey: 'bookingForm.sections.when',
          descriptionKey: 'bookingForm.sections.whenDescription',
          iconSrc: calendarIcon,
          iconTone: 'primary',
          tipKey: 'bookingForm.messages.tip',
          fields: [
            {
              key: 'eventDateRange',
              type: 'daterange',
              labelKey: 'bookingForm.fields.dateRange.label',
              placeholderKey: 'bookingForm.fields.dateRange.placeholder',
              colspan: 2,
              validation: [
                { type: 'required', messageKey: 'bookingForm.validation.required' },
                {
                  type: 'dateRangeComplete',
                  messageKey: 'bookingForm.validation.dateRangeComplete',
                },
                { type: 'futureDate', messageKey: 'bookingForm.validation.futureDate' },
                { type: 'dateRangeOrder', messageKey: 'bookingForm.validation.dateRangeOrder' },
                {
                  type: 'maxBookingWindow',
                  value: 1,
                  messageKey: 'bookingForm.validation.maxBookingWindow',
                },
                {
                  type: 'maxEventDuration',
                  value: 1,
                  messageKey: 'bookingForm.validation.maxEventDuration',
                },
              ],
            },
            {
              key: 'eventTimeRange',
              type: 'timerange',
              labelKey: 'bookingForm.fields.timeRange.label',
              placeholderKey: 'bookingForm.fields.timeRange.placeholder',
              colspan: 2,
              validation: [
                { type: 'required', messageKey: 'bookingForm.validation.required' },
                {
                  type: 'timeRangeComplete',
                  messageKey: 'bookingForm.validation.timeRangeComplete',
                },
                { type: 'timeRangeOrder', messageKey: 'bookingForm.validation.timeRangeOrder' },
              ],
            },
          ],
        },
        {
          headerKey: 'bookingForm.sections.attendees',
          descriptionKey: 'bookingForm.sections.attendeesDescription',
          iconSrc: peopleIcon,
          iconTone: 'primary',
          fields: [
            {
              key: 'numberOfAttendees',
              type: 'number',
              labelKey: 'bookingForm.fields.numberOfAttendees.label',
              placeholderKey: 'bookingForm.fields.numberOfAttendees.placeholder',
              colspan: 1,
              validation: [
                { type: 'required', messageKey: 'bookingForm.validation.required' },
                { type: 'min', value: 1, messageKey: 'bookingForm.validation.min' },
                { type: 'max', value: 2000, messageKey: 'bookingForm.validation.max' },
              ],
            },
          ],
        },
      ],
    },

    // Step 2: Available Spaces
    {
      id: 'spaceSelection',
      titleKey: 'bookingForm.steps.spaceSelection.title',
      descriptionKey: 'bookingForm.steps.spaceSelection.description',
      bannerTitleKey: 'bookingForm.steps.spaceSelection.bannerTitle',
      bannerDescriptionKey: 'bookingForm.steps.spaceSelection.bannerDescription',
      type: 'space-selection',
      section: 'spaceSelection',
    },

    // Step 3: Event Details - per Figma: Event name first (full width), then dropdowns
    {
      id: 'eventDetails',
      titleKey: 'bookingForm.steps.eventDetails.title',
      bannerTitleKey: 'bookingForm.steps.eventDetails.bannerTitle',
      bannerDescriptionKey: 'bookingForm.steps.eventDetails.bannerDescription',
      type: 'form',
      section: 'eventDetails',
      sections: [
        {
          headerKey: 'bookingForm.sections.eventInfo',
          descriptionKey: 'bookingForm.sections.eventInfoDescription',
          iconSrc: eventInfoIcon,
          iconTone: 'primary',
          fields: [
            {
              key: 'eventTitle',
              type: 'text',
              labelKey: 'bookingForm.fields.eventTitle.label',
              placeholderKey: 'bookingForm.fields.eventTitle.placeholder',
              colspan: 2,
              validation: [
                { type: 'required', messageKey: 'bookingForm.validation.required' },
                { type: 'maxLength', value: 100, messageKey: 'bookingForm.validation.maxLength' },
              ],
            },
            {
              key: 'visitPurposeCode',
              type: 'dropdown',
              labelKey: 'bookingForm.fields.visitPurposeCode.label',
              placeholderKey: 'bookingForm.fields.visitPurposeCode.placeholder',
              colspan: 1,
              validation: [{ type: 'required', messageKey: 'bookingForm.validation.required' }],
              options: VISIT_PURPOSE_OPTIONS,
            },
            {
              key: 'businessGroupCode',
              type: 'dropdown',
              labelKey: 'bookingForm.fields.businessGroupCode.label',
              placeholderKey: 'bookingForm.fields.businessGroupCode.placeholder',
              colspan: 1,
              validation: [{ type: 'required', messageKey: 'bookingForm.validation.required' }],
              options: [],
            },
            {
              key: 'businessJustification',
              type: 'text',
              labelKey: 'bookingForm.fields.businessJustification.label',
              placeholderKey: 'bookingForm.fields.businessJustification.placeholder',
              colspan: 1,
              validation: [{ type: 'required', messageKey: 'bookingForm.validation.required' }],
            },
            {
              key: 'attendeeTypeCode',
              type: 'dropdown',
              labelKey: 'bookingForm.fields.attendeeTypeCode.label',
              placeholderKey: 'bookingForm.fields.attendeeTypeCode.placeholder',
              colspan: 1,
              validation: [{ type: 'required', messageKey: 'bookingForm.validation.required' }],
              options: ATTENDEE_TYPE_OPTIONS,
            },
          ],
        },
        {
          headerKey: 'bookingForm.sections.sponsorInfo',
          descriptionKey: 'bookingForm.sections.sponsorInfoDescription',
          iconSrc: sponsorshipIcon,
          iconTone: 'primary',
          fields: [
            {
              key: 'sponsor.name',
              type: 'people-picker',
              labelKey: 'bookingForm.fields.sponsorName.label',
              placeholderKey: 'bookingForm.peoplePicker.placeholder',
              colspan: 1,
              validation: [{ type: 'required', messageKey: 'bookingForm.validation.required' }],
            },
            {
              key: 'sponsor.costCenter',
              type: 'text',
              labelKey: 'bookingForm.fields.sponsorCostCenter.label',
              placeholderKey: 'bookingForm.fields.sponsorCostCenter.placeholder',
              colspan: 1,
              helpLink: 'bookingForm.fields.sponsorCostCenter.helpLink',
              helpTooltipKey: 'bookingForm.fields.sponsorCostCenter.helpTooltip',
              validation: [{ type: 'required', messageKey: 'bookingForm.validation.required' }],
            },
          ],
        },
        {
          headerKey: 'bookingForm.sections.onSiteContact',
          descriptionKey: 'bookingForm.sections.onSiteContactDescription',
          iconSrc: onsiteContactIcon,
          iconTone: 'primary',
          fields: [
            {
              key: 'hasOnSiteContact',
              type: 'radio',
              labelKey: 'bookingForm.fields.hasOnSiteContact.label',
              colspan: 2,
              defaultValue: 'no',
              // Radio with default 'no' is never empty (radios can't be deselected),
              // so a `required` rule can never fire — omitted intentionally.
              options: [
                { value: 'yes', labelKey: 'bookingForm.options.yes' },
                { value: 'no', labelKey: 'bookingForm.options.no' },
              ],
            },
            {
              key: 'onSiteContacts',
              type: 'contact-list',
              labelKey: 'bookingForm.fields.onSiteContacts.label',
              colspan: 2,
              visibleWhen: { fieldKey: 'hasOnSiteContact', operator: 'equals', value: 'yes' },
              validation: [
                { type: 'required', messageKey: 'bookingForm.validation.required' },
                { type: 'contactList', messageKey: 'bookingForm.validation.contactList' },
              ],
            },
            {
              key: 'consentAcknowledgement',
              type: 'checkbox',
              labelKey: 'bookingForm.consent.label',
              colspan: 2,
              bannerVariant: 'salmon',
              visibleWhen: { fieldKey: 'hasOnSiteContact', operator: 'equals', value: 'yes' },
              validation: [
                { type: 'required', messageKey: 'bookingForm.validation.consentRequired' },
              ],
            },
          ],
        },
      ],
    },

    // Step 4: Additional Details (merged - catering, filming, overnight, shipments, additional info, policies)
    {
      id: 'additionalDetails',
      titleKey: 'bookingForm.steps.additionalDetails.title',
      bannerTitleKey: 'bookingForm.steps.additionalDetails.bannerTitle',
      bannerDescriptionKey: 'bookingForm.steps.additionalDetails.bannerDescription',
      type: 'form',
      section: 'additionalDetails',
      sections: [
        {
          headerKey: 'bookingForm.sections.cateringDetails',
          descriptionKey: 'bookingForm.sections.cateringDescription',
          iconSrc: cateringIcon,
          iconTone: 'primary',
          fields: [
            {
              key: 'hasCatering',
              type: 'radio',
              labelKey: 'bookingForm.fields.hasCatering.label',
              colspan: 2,
              defaultValue: 'no',
              options: [
                { value: 'yes', labelKey: 'bookingForm.options.yes' },
                { value: 'no', labelKey: 'bookingForm.options.no' },
              ],
            },
          ],
        },
        {
          headerKey: 'bookingForm.sections.filming',
          descriptionKey: 'bookingForm.sections.filmingDescription',
          iconSrc: filmingIcon,
          iconTone: 'primary',
          fields: [
            {
              key: 'filmingRequired',
              type: 'radio',
              labelKey: 'bookingForm.fields.filmingRequired.label',
              colspan: 2,
              defaultValue: 'no',
              options: [
                { value: 'yes', labelKey: 'bookingForm.options.yes' },
                { value: 'no', labelKey: 'bookingForm.options.no' },
              ],
            },
          ],
        },
        {
          headerKey: 'bookingForm.sections.overnightStorage',
          descriptionKey: 'bookingForm.sections.overnightStorageDescription',
          iconSrc: overnightStorageIcon,
          iconTone: 'primary',
          fields: [
            {
              key: 'overnightStorageRequired',
              type: 'radio',
              labelKey: 'bookingForm.fields.overnightStorageRequired.label',
              colspan: 2,
              defaultValue: 'no',
              options: [
                { value: 'yes', labelKey: 'bookingForm.options.yes' },
                { value: 'no', labelKey: 'bookingForm.options.no' },
              ],
            },
          ],
        },
        {
          headerKey: 'bookingForm.sections.shipments',
          descriptionKey: 'bookingForm.sections.shipmentsDescription',
          iconSrc: shipmentsIcon,
          iconTone: 'primary',
          fields: [
            {
              key: 'shipmentsExpected',
              type: 'radio',
              labelKey: 'bookingForm.fields.shipmentsExpected.label',
              colspan: 2,
              defaultValue: 'no',
              options: [
                { value: 'yes', labelKey: 'bookingForm.options.yes' },
                { value: 'no', labelKey: 'bookingForm.options.no' },
              ],
            },
          ],
        },
        {
          headerKey: 'bookingForm.sections.additionalInfo',
          descriptionKey: 'bookingForm.sections.additionalInfoDescription',
          iconSrc: additionalInfoIcon,
          iconTone: 'primary',
          fields: [
            {
              key: 'additionalInformation',
              type: 'textarea',
              labelKey: 'bookingForm.fields.additionalInformation.label',
              placeholderKey: 'bookingForm.fields.additionalInformation.placeholder',
              colspan: 2,
              validation: [
                { type: 'maxLength', value: 500, messageKey: 'bookingForm.validation.maxLength' },
              ],
            },
          ],
        },
        {
          headerKey: 'bookingForm.sections.compliance',
          descriptionKey: 'bookingForm.sections.complianceDescription',
          iconSrc: complianceIcon,
          iconTone: 'warning',
          fields: [
            {
              key: 'policyAcknowledgement',
              type: 'checkbox',
              labelKey: 'bookingForm.policyAcknowledgement.label',
              linkText: 'bookingForm.policyAcknowledgement.linkText',
              linkUrl: DEFAULT_POLICIES_URL,
              colspan: 2,
              validation: [
                { type: 'required', messageKey: 'bookingForm.validation.policyRequired' },
              ],
            },
          ],
        },
      ],
    },

    // Step 5: Review & Confirm
    {
      id: 'review',
      titleKey: 'bookingForm.steps.review.title',
      descriptionKey: 'bookingForm.steps.review.description',
      bannerTitleKey: 'bookingForm.steps.review.bannerTitle',
      bannerDescriptionKey: 'bookingForm.steps.review.bannerDescription',
      type: 'review',
      section: 'review',
    },
  ],
};
